/*************************************************************
 * ModelPerformanceTab.js - Model performance analysis tab
 *************************************************************/

import { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../context/AppContext.jsx';
import { api } from '../api/reportingApi.js';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.jsx';
import { ErrorBoundary } from '../components/ui/ErrorBoundary.jsx';
import { ModelPerformanceChart } from '../components/charts/ModelPerformanceChart.jsx';

export const ModelPerformanceTab = () => {
    const { state, setLoading, setError, addNotification } = useAppContext();
    const [performanceData, setPerformanceData] = useState(null);

    const loadPerformance = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const data = await api.getModelPerformance(state.filters);
            setPerformanceData(data);

            addNotification({
                type: 'success',
                message: 'Model performance data loaded',
                duration: 3000
            });
        } catch (error) {
            console.error('[ModelPerformance] Error loading:', error);
            setError(error);
            addNotification({
                type: 'danger',
                message: 'Failed to load model performance: ' + error.message
            });
        } finally {
            setLoading(false);
        }
    }, [state.filters, setLoading, setError, addNotification]);

    useEffect(() => {
        loadPerformance();
    }, [loadPerformance]);

    if (state.loading) {
        return (
            <LoadingSpinner
                message="Analyzing model performance..."
                size="large"
            />
        );
    }

    return (
        <ErrorBoundary error={state.error} onRetry={loadPerformance}>
            <div className="model-performance-content fade-in">
                {performanceData && (
                    <div>
                        <div className="row mb-4">
                            <div className="col-md-8">
                                <div className="card shadow-sm">
                                    <div className="card-header">
                                        <h5 className="mb-0">
                                            <i className="fas fa-chart-radar text-primary mr-2"></i>
                                            Performance Metrics
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        <ModelPerformanceChart
                                            data={performanceData}
                                            height={400}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="card shadow-sm">
                                    <div className="card-header">
                                        <h5 className="mb-0">
                                            <i className="fas fa-tachometer-alt text-primary mr-2"></i>
                                            Overall Score
                                        </h5>
                                    </div>
                                    <div className="card-body text-center">
                                        <h1 className={`text-${
                                            performanceData.overall_score > 80 ? 'success' :
                                            performanceData.overall_score > 60 ? 'warning' : 'danger'
                                        }`}>
                                            {performanceData.overall_score.toFixed(1)}%
                                        </h1>
                                        <p className="text-muted mb-4">Overall Model Performance</p>
                                        <hr />
                                        <div className="text-left">
                                            {performanceData.accuracy_metrics && (
                                                <div>
                                                    <p className="mb-2">
                                                        <strong>Accuracy: </strong>
                                                        {(performanceData.accuracy_metrics.overall_accuracy * 100).toFixed(1)}%
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Precision: </strong>
                                                        {(performanceData.accuracy_metrics.precision * 100).toFixed(1)}%
                                                    </p>
                                                    <p className="mb-2">
                                                        <strong>Recall: </strong>
                                                        {(performanceData.accuracy_metrics.recall * 100).toFixed(1)}%
                                                    </p>
                                                    <p className="mb-0">
                                                        <strong>F1 Score: </strong>
                                                        {(performanceData.accuracy_metrics.f1_score * 100).toFixed(1)}%
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row mb-4">
                            <div className="col-md-6">
                                <div className="card shadow-sm">
                                    <div className="card-header">
                                        <h5 className="mb-0">
                                            <i className="fas fa-handshake text-primary mr-2"></i>
                                            Model Agreement Analysis
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {performanceData.agreement_analysis &&
                                            Object.entries(performanceData.agreement_analysis).map(([agreement, data]) => (
                                                <div key={agreement} className="mb-3">
                                                    <div className="d-flex justify-content-between mb-1">
                                                        <span>
                                                            {agreement.charAt(0).toUpperCase() + agreement.slice(1)}
                                                        </span>
                                                        <span>
                                                            {data.count} ({data.percentage.toFixed(1)}%)
                                                        </span>
                                                    </div>
                                                    <div className="progress">
                                                        <div
                                                            className={`progress-bar bg-${
                                                                agreement === 'unanimous' ? 'success' :
                                                                agreement === 'majority' ? 'info' : 'warning'
                                                            }`}
                                                            style={{ width: `${data.percentage}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="card shadow-sm">
                                    <div className="card-header">
                                        <h5 className="mb-0">
                                            <i className="fas fa-chart-bar text-primary mr-2"></i>
                                            Confidence Distribution
                                        </h5>
                                    </div>
                                    <div className="card-body">
                                        {performanceData.confidence_distribution?.distribution &&
                                            performanceData.confidence_distribution.distribution.map((bucket, idx) => (
                                                <div key={idx} className="mb-2">
                                                    <div className="d-flex justify-content-between">
                                                        <small>
                                                            {bucket._id === 'unknown' ? 'Unknown' : 
                                                                `${(bucket._id * 100).toFixed(0)}-${((bucket._id + 0.1) * 100).toFixed(0)}%`}
                                                        </small>
                                                        <small>{bucket.count}</small>
                                                    </div>
                                                    <div className="progress">
                                                        <div
                                                            className="progress-bar"
                                                            style={{ width: `${(bucket.count / 100)}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                        {performanceData.recommendations && performanceData.recommendations.length > 0 && (
                            <div className="row">
                                <div className="col-12">
                                    <div className="card shadow-sm">
                                        <div className="card-header bg-info text-white">
                                            <h5 className="mb-0">
                                                <i className="fas fa-lightbulb mr-2"></i>
                                                Model Improvement Recommendations
                                            </h5>
                                        </div>
                                        <div className="card-body">
                                            <ul className="mb-0">
                                                {performanceData.recommendations.map((rec, idx) => (
                                                    <li key={idx}>{rec}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </ErrorBoundary>
    );
};